import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Header from 'shared/components/Header';
import ClockLoader from 'shared/components/ClockLoader';
import { useAuth } from 'shared/hooks/useAuth';
import {
  REGISTER_WIZARD_STEPS,
  createInitialWizardState,
  validateAllWizardSteps,
  validateWizardStep,
} from '../../../config/wizardSteps';
import useSoftwareService from '../../../hooks/useSoftwareService';
import { buildCoreStepPayload, buildWizardPayload } from '../../../utils/wizardPayloadBuilder';
import { detailToWizardState } from '../../../utils/wizardStateMapper';
import { apiErrorMessage } from '../../../utils/responseHelpers';
import WizardStepApprovalSignOff from './WizardStepApprovalSignOff';
import WizardStepChangeManagement from './WizardStepChangeManagement';
import WizardStepIntegration from './WizardStepIntegration';
import WizardStepComplianceGovernance from './WizardStepComplianceGovernance';
import WizardStepIncidents from './WizardStepIncidents';
import WizardStepMonitoringReview from './WizardStepMonitoringReview';
import WizardStepRiskImpact from './WizardStepRiskImpact';
import WizardStepSystemIdentification from './WizardStepSystemIdentification';
import WizardStepUseCase from './WizardStepUseCase';
import WizardStepViewReport from './WizardStepViewReport';

const WIZARD_SNAPSHOT_KEY = 'sr-register-wizard-snapshot';

const REPORT_STEP_INDEX = REGISTER_WIZARD_STEPS.findIndex((step) => step.id === 'viewReport');

function resolveStepIndexFromParam(stepParam) {
  if (stepParam == null) return null;
  const normalized = String(stepParam).trim().toLowerCase();
  if (normalized === 'report' || normalized === 'viewreport') {
    return REPORT_STEP_INDEX >= 0 ? REPORT_STEP_INDEX : null;
  }
  const byId = REGISTER_WIZARD_STEPS.findIndex((step) => step.id.toLowerCase() === normalized);
  return byId >= 0 ? byId : null;
}

const cloneWizardData = (data) => JSON.parse(JSON.stringify(data));

const RegisterSoftwareWizardPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: routeId } = useParams();
  const isEditMode = Boolean(routeId);
  const { sessionLoading } = useAuth();
  const { createItem, updateItem, loadLookups, lookups, getItem } = useSoftwareService();

  const stepQuery = searchParams.get('step');
  const initialStepFromUrl = resolveStepIndexFromParam(stepQuery);
  const [activeStep, setActiveStep] = useState(
    () => (initialStepFromUrl != null ? initialStepFromUrl : 0)
  );
  const [wizardData, setWizardData] = useState(createInitialWizardState);
  const [lastSavedData, setLastSavedData] = useState(() => cloneWizardData(createInitialWizardState()));
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(isEditMode);
  const [completedSteps, setCompletedSteps] = useState(() => new Set());
  const [leaveDialog, setLeaveDialog] = useState(null);

  const step = REGISTER_WIZARD_STEPS[activeStep];
  const isFirst = activeStep === 0;
  const isLast = activeStep === REGISTER_WIZARD_STEPS.length - 1;
  const totalSteps = REGISTER_WIZARD_STEPS.length;
  const systemName = (wizardData.softwareIdentification?.softwareName ?? '').trim();
  const deploymentStatus = wizardData.softwareIdentification?.deploymentStatus ?? '';

  const isDirty = useMemo(() => {
    if (lastSavedData == null) return true;
    try {
      return JSON.stringify(wizardData) !== JSON.stringify(lastSavedData);
    } catch {
      return true;
    }
  }, [wizardData, lastSavedData]);

  const markSaved = useCallback((data) => {
    setLastSavedData(cloneWizardData(data));
  }, []);

  const saveWizardSnapshot = useCallback(() => {
    sessionStorage.setItem(
      WIZARD_SNAPSHOT_KEY,
      JSON.stringify({
        wizardData,
        activeStep,
        completedSteps: Array.from(completedSteps),
        lastSavedData,
      })
    );
  }, [wizardData, activeStep, completedSteps, lastSavedData]);

  useEffect(() => {
    if (sessionLoading) return;

    const urlStepIndex = resolveStepIndexFromParam(stepQuery);

    try {
      const raw = sessionStorage.getItem(WIZARD_SNAPSHOT_KEY);
      if (raw) {
        const snap = JSON.parse(raw);
        if (snap?.wizardData) {
          setWizardData(snap.wizardData);
          if (urlStepIndex != null) {
            setActiveStep(urlStepIndex);
          } else if (typeof snap.activeStep === 'number') {
            setActiveStep(snap.activeStep);
          }
          if (Array.isArray(snap.completedSteps)) setCompletedSteps(new Set(snap.completedSteps));
          if (snap.lastSavedData) {
            setLastSavedData(snap.lastSavedData);
          } else {
            setLastSavedData(null);
          }
        }
        sessionStorage.removeItem(WIZARD_SNAPSHOT_KEY);
        setLoadingDetail(false);
        loadLookups().then((result) => {
          if (result?.ok === false) {
            toast.error(apiErrorMessage(result.error, 'Failed to load lookup lists. Check login and tenant.'));
          }
        });
        return undefined;
      }
    } catch {
      sessionStorage.removeItem(WIZARD_SNAPSHOT_KEY);
    }

    let cancelled = false;

    const load = async () => {
      if (isEditMode) {
        setLoadingDetail(true);
      }
      try {
        const lookupPromise = loadLookups();
        if (isEditMode) {
          const [lookupResult, detail] = await Promise.all([lookupPromise, getItem(routeId)]);
          if (cancelled) return;
          if (lookupResult?.ok === false) {
            toast.error(
              apiErrorMessage(lookupResult.error, 'Failed to load lookup lists. Check login and tenant.')
            );
          }
          if (!detail?.id) {
            toast.error('Software not found.');
            navigate('/system-register/software');
            return;
          }
          const mapped = detailToWizardState(detail, lookupResult?.lookups?.regulations ?? []);
          setWizardData(mapped);
          markSaved(mapped);
          setCompletedSteps(new Set(REGISTER_WIZARD_STEPS.map((_, index) => index)));
          if (urlStepIndex != null) {
            setActiveStep(urlStepIndex);
          }
        } else {
          const lookupResult = await lookupPromise;
          if (!cancelled && lookupResult?.ok === false) {
            toast.error(
              apiErrorMessage(lookupResult.error, 'Failed to load lookup lists. Check login and tenant.')
            );
          }
        }
      } catch (error) {
        if (!cancelled && isEditMode) {
          toast.error(apiErrorMessage(error, 'Failed to load software details.'));
          navigate('/system-register/software');
        }
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, routeId, sessionLoading, getItem, loadLookups, navigate, markSaved, stepQuery]);

  const updateStep = useCallback((stateKey, updater) => {
    setWizardData((prev) => ({
      ...prev,
      [stateKey]: typeof updater === 'function' ? updater(prev[stateKey]) : updater,
    }));
  }, []);

  const goToStep = (index) => {
    if (index < 0 || index >= totalSteps) return;
    setErrors({});
    setTouched({});
    setActiveStep(index);
  };

  const requestGoToStep = (index) => {
    if (index < 0 || index >= totalSteps || index === activeStep) return;
    if (isDirty) {
      setLeaveDialog({ type: 'step', target: index });
      return;
    }
    goToStep(index);
  };

  const listPath = isEditMode ? '/system-register/software' : '/system-register';

  const requestLeaveWizard = (path = listPath) => {
    if (isDirty) {
      setLeaveDialog({ type: 'route', path });
      return;
    }
    navigate(path);
  };

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!isDirty || submitting) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty, submitting]);

  const persistIdentificationStep = async (data) => {
    const payload = buildCoreStepPayload(data);
    const existingId = data.softwareId ?? (isEditMode ? Number(routeId) : null);
    if (existingId) {
      const updated = await updateItem(existingId, payload);
      let next = data;
      if (updated?.deploymentStatuses) {
        next = {
          ...data,
          softwareIdentification: {
            ...data.softwareIdentification,
            deploymentStatusesList: updated.deploymentStatuses,
            deploymentStatus: updated.deploymentStatus ?? data.softwareIdentification?.deploymentStatus,
          },
        };
        setWizardData(next);
      }
      return { id: existingId, data: next };
    }
    const response = await createItem(payload);
    const created = typeof response === 'object' && response?.id != null ? response : null;
    let next = data;
    if (created?.deploymentStatuses) {
      next = {
        ...data,
        softwareIdentification: {
          ...data.softwareIdentification,
          deploymentStatusesList: created.deploymentStatuses,
          deploymentStatus: created.deploymentStatus ?? data.softwareIdentification?.deploymentStatus,
        },
      };
    }
    return { id: created?.id ?? null, data: next };
  };

  const persistFullWizard = async (systemId, data) => {
    const payload = buildWizardPayload(data);
    await updateItem(systemId, payload);
  };

  const persistCurrentStep = async () => {
    const stepErrors = await validateWizardStep(step, wizardData);
    if (Object.keys(stepErrors).length) {
      setErrors(stepErrors);
      setTouched(Object.fromEntries(Object.keys(stepErrors).map((k) => [k, true])));
      toast.error('Please complete the required fields for this step.');
      return null;
    }

    setSubmitting(true);
    try {
      let nextData = wizardData;

      if (step.id === 'softwareIdentification') {
        const result = await persistIdentificationStep(wizardData);
        if (!result?.id) {
          toast.error('Software was saved but no id was returned.');
          return null;
        }
        nextData = { ...result.data, softwareId: result.id };
        setWizardData(nextData);
        toast.success(
          isEditMode ? 'Identification saved.' : 'System identification saved — visible on the systems list.'
        );
      } else if (!isLast) {
        const systemId = wizardData.softwareId ?? (isEditMode ? Number(routeId) : null);
        if (systemId) {
          await persistFullWizard(systemId, wizardData);
          if (step.id === 'useCase') {
            try {
              const detail = await getItem(systemId);
              if (detail) {
                const mapped = detailToWizardState(detail, lookups?.regulations ?? []);
                nextData = {
                  ...wizardData,
                  meta: {
                    ...wizardData.meta,
                    businessCaseId: mapped.meta?.businessCaseId ?? null,
                  },
                  useCase: mapped.useCase,
                };
                setWizardData(nextData);
              }
            } catch {
              // Keep local wizard state if refresh fails.
            }
            toast.success('Business case and use case saved.');
          } else if (step.id === 'monitoringReview') {
            toast.success('Monitoring reviews saved to this software.');
          } else if (step.id === 'incidentEscalation') {
            toast.success('Incidents saved to this software.');
          } else if (step.id === 'changeManagement') {
            toast.success('Change management records saved to this software.');
          } else if (step.id === 'integration') {
            toast.success('Integration details saved to this software.');
          } else {
            toast.success('Changes saved.');
          }
        }
      } else {
        const allErrors = await validateAllWizardSteps(wizardData);
        if (Object.keys(allErrors).length) {
          setErrors(allErrors);
          setTouched(Object.fromEntries(Object.keys(allErrors).map((k) => [k, true])));
          toast.error('Please complete all required sections.');
          return null;
        }
        const payload = buildWizardPayload(wizardData);
        const systemId = wizardData.softwareId ?? (isEditMode ? Number(routeId) : null);
        if (systemId) {
          await updateItem(systemId, payload);
        } else {
          const created = await createItem(payload);
          if (created?.id) {
            nextData = { ...wizardData, softwareId: created.id };
            setWizardData(nextData);
          }
        }
        toast.success(isEditMode ? 'Software updated successfully' : 'Software registered successfully');
      }

      setErrors({});
      setTouched({});
      setCompletedSteps((prev) => new Set(prev).add(activeStep));
      markSaved(nextData);
      return nextData;
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save software.'));
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAndContinue = async () => {
    const saved = await persistCurrentStep();
    if (!saved) return;

    if (isLast) {
      navigate('/system-register/software');
      return;
    }
    setActiveStep((s) => s + 1);
  };

  const closeLeaveDialog = () => {
    setLeaveDialog(null);
  };

  const handleDiscardAndLeave = () => {
    const dialog = leaveDialog;
    if (!dialog) return;

    if (lastSavedData) {
      setWizardData(cloneWizardData(lastSavedData));
    }
    setLeaveDialog(null);
    setErrors({});
    setTouched({});

    if (dialog.type === 'step') {
      goToStep(dialog.target);
      return;
    }
    if (dialog.type === 'route' && dialog.path) {
      navigate(dialog.path);
    }
  };

  const handleSaveAndLeave = async () => {
    const dialog = leaveDialog;
    if (!dialog) return;

    const saved = await persistCurrentStep();
    if (!saved) return;

    setLeaveDialog(null);

    if (dialog.type === 'step') {
      goToStep(dialog.target);
      return;
    }
    if (dialog.type === 'route' && dialog.path) {
      navigate(dialog.path);
    }
  };

  const stepFieldProps = (stateKey) => ({
    values: wizardData[stateKey],
    errors,
    touched,
    onChange: (name, val) => updateStep(stateKey, (prev) => ({ ...prev, [name]: val })),
    onSetField: (name, val) => updateStep(stateKey, (prev) => ({ ...prev, [name]: val })),
  });

  const renderStepContent = () => {
    switch (step.id) {
      case 'softwareIdentification':
        return (
          <WizardStepSystemIdentification
            values={wizardData.softwareIdentification}
            errors={errors}
            touched={touched}
            onChange={(name, val) =>
              updateStep('softwareIdentification', (prev) => ({ ...prev, [name]: val }))
            }
            onSetField={(name, val) =>
              updateStep('softwareIdentification', (prev) => ({ ...prev, [name]: val }))
            }
            onLookupChange={(name, val) =>
              updateStep('softwareIdentification', (prev) => ({ ...prev, [name]: val }))
            }
            lookups={lookups}
            onRefreshLookups={loadLookups}
            systemName={systemName}
          />
        );
      case 'useCase':
        return (
          <WizardStepUseCase
            {...stepFieldProps('useCase')}
            lookups={lookups}
            onRefreshLookups={loadLookups}
            onBeforeView={saveWizardSnapshot}
            softwareName={systemName}
            softwareId={wizardData.softwareId ?? (isEditMode ? Number(routeId) : null)}
            documents={wizardData.complianceGovernance?.documents ?? []}
            onDocumentsChange={(docs) =>
              updateStep('complianceGovernance', (prev) => ({ ...prev, documents: docs }))
            }
          />
        );
      case 'riskImpact':
        return (
          <WizardStepRiskImpact
            {...stepFieldProps('riskImpact')}
            dataModelValues={wizardData.dataModel}
            onDataModelReplace={(next) => updateStep('dataModel', next)}
            onWizardMetaPatch={(patch) =>
              updateStep('meta', (prev) => ({ ...prev, ...(patch || {}) }))
            }
            lookups={lookups}
            onRefreshLookups={loadLookups}
            systemName={systemName}
            onBeforeView={saveWizardSnapshot}
            softwareName={systemName}
            softwareId={wizardData.softwareId ?? (isEditMode ? Number(routeId) : null)}
          />
        );
      case 'complianceGovernance':
        return (
          <WizardStepComplianceGovernance
            values={wizardData.complianceGovernance}
            errors={errors}
            touched={touched}
            onChange={(name, val) =>
              updateStep('complianceGovernance', (prev) => ({ ...prev, [name]: val }))
            }
            onSetField={(name, val) =>
              updateStep('complianceGovernance', (prev) => ({ ...prev, [name]: val }))
            }
            onLookupChange={(name, val) =>
              updateStep('complianceGovernance', (prev) => ({ ...prev, [name]: val }))
            }
          />
        );
      case 'monitoringReview':
        return (
          <WizardStepMonitoringReview
            {...stepFieldProps('monitoringReview')}
            onBeforeView={saveWizardSnapshot}
            softwareName={systemName}
            softwareId={wizardData.softwareId ?? (isEditMode ? Number(routeId) : null)}
          />
        );
      case 'incidentEscalation':
        return (
          <WizardStepIncidents
            {...stepFieldProps('incidentEscalation')}
            onBeforeView={saveWizardSnapshot}
            softwareName={systemName}
            softwareId={wizardData.softwareId ?? (isEditMode ? Number(routeId) : null)}
          />
        );
      case 'changeManagement':
        return (
          <WizardStepChangeManagement
            {...stepFieldProps('changeManagement')}
            onBeforeView={saveWizardSnapshot}
            softwareName={systemName}
            softwareId={wizardData.softwareId ?? (isEditMode ? Number(routeId) : null)}
          />
        );
      case 'integration':
        return (
          <WizardStepIntegration
            {...stepFieldProps('integration')}
            softwareId={wizardData.softwareId ?? (isEditMode ? Number(routeId) : null)}
            softwareName={systemName}
          />
        );
      case 'approvalSignOff':
        return (
          <WizardStepApprovalSignOff
            softwareId={wizardData.softwareId ?? (isEditMode ? Number(routeId) : null)}
            identificationValues={wizardData.softwareIdentification}
            onIdentificationSetField={(name, val) =>
              updateStep('softwareIdentification', (prev) => ({ ...prev, [name]: val }))
            }
          />
        );
      case 'viewReport':
        return (
          <WizardStepViewReport
            wizardData={wizardData}
            lookups={lookups}
            softwareId={wizardData.softwareId ?? (isEditMode ? Number(routeId) : null)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      m={{ xs: '8px', sm: '12px' }}
      sx={{
        height: 'calc(100vh - 96px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Header title={isEditMode ? 'System Register' : 'Software Registration'} />

      <Paper
        sx={{
          mt: 1,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            pt: systemName ? 1.5 : 1.25,
            pb: 1.25,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : alpha(theme.palette.background.default, 0.6),
          }}
        >
          {systemName ? (
            <Box
              sx={{
                mb: 1.25,
                px: 1.5,
                py: 1.1,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                minWidth: 0,
                background: `linear-gradient(
                  105deg,
                  ${alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.22 : 0.1)} 0%,
                  ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.05)} 100%
                )`,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.28)}`,
                borderLeft: `4px solid ${theme.palette.secondary.main}`,
              }}
            >
              <Box minWidth={0} flex={1}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    letterSpacing: '0.02em',
                    fontWeight: 700,
                    color: 'text.secondary',
                    lineHeight: 1.2,
                    mb: 0.25,
                  }}
                >
                  System
                </Typography>
                <Typography
                  variant="h6"
                  component="h2"
                  title={systemName}
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.25,
                    letterSpacing: '-0.01em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: 'text.primary',
                  }}
                >
                  {systemName}
                </Typography>
              </Box>
              {deploymentStatus ? (
                <Chip
                  label={String(deploymentStatus).replace(/_/g, ' ')}
                  size="small"
                  variant="outlined"
                  sx={{
                    flexShrink: 0,
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    borderColor: alpha(theme.palette.secondary.main, 0.4),
                    bgcolor: alpha(theme.palette.background.paper, 0.7),
                  }}
                />
              ) : null}
            </Box>
          ) : null}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, maxHeight: 80, overflowY: 'auto' }}>
            {REGISTER_WIZARD_STEPS.map((s, index) => {
              const isActive = index === activeStep;
              const isDone = completedSteps.has(index);
              return (
                <Chip
                  key={s.id}
                  label={`${index + 1}. ${s.label}`}
                  size="small"
                  clickable
                  color={isActive ? 'secondary' : isDone ? 'primary' : 'default'}
                  variant={isActive ? 'filled' : 'outlined'}
                  onClick={() => requestGoToStep(index)}
                  sx={{ fontWeight: isActive ? 700 : 600, fontSize: '0.72rem', maxWidth: '100%' }}
                />
              );
            })}
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: { xs: 1.5, sm: 2 }, py: 1.5 }}>
          {loadingDetail ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
              <ClockLoader message="Loading software details…" />
            </Box>
          ) : (
            <>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    bgcolor: alpha(theme.palette.secondary.main, 0.15),
                    color: theme.palette.secondary.main,
                    flexShrink: 0,
                  }}
                >
                  {activeStep + 1}
                </Box>
                <Box minWidth={0} flex={1}>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                      {step.label}
                    </Typography>
                    {isDirty ? (
                      <Chip label="Unsaved changes" size="small" color="warning" variant="outlined" />
                    ) : null}
                  </Box>
                  {step.description ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {step.description}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
              {renderStepContent()}
            </>
          )}
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={{ xs: 1.5, sm: 2 }}
          py={1.25}
          borderTop={1}
          borderColor="divider"
          flexShrink={0}
        >
          <Button
            size="small"
            onClick={() => requestLeaveWizard(listPath)}
            disabled={submitting || loadingDetail}
          >
            {isEditMode ? 'Back to list' : 'Cancel'}
          </Button>
          <Box display="flex" gap={1}>
            {!isFirst ? (
              <Button
                size="small"
                onClick={() => requestGoToStep(activeStep - 1)}
                disabled={submitting || loadingDetail}
              >
                Back
              </Button>
            ) : null}
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={handleSaveAndContinue}
              disabled={submitting || loadingDetail}
            >
              {submitting ? (
                <ClockLoader inline size={22} />
              ) : isLast ? (
                isEditMode ? 'Save changes' : 'Register software'
              ) : (
                'Save and continue'
              )}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Dialog open={Boolean(leaveDialog)} onClose={closeLeaveDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Unsaved changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes on this step. Save and continue before leaving, or discard
            your changes.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={closeLeaveDialog} disabled={submitting} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDiscardAndLeave} disabled={submitting} color="warning">
            Discard
          </Button>
          <Button
            onClick={handleSaveAndLeave}
            disabled={submitting}
            variant="contained"
            color="secondary"
          >
            {submitting ? <ClockLoader inline size={22} /> : 'Save and continue'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegisterSoftwareWizardPage;
